import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import OfferImageField from "@/components/OfferImageField";
import { OfferThumb, formatPrice } from "@/components/OfferCard";
import { ArrowUpDown, ArrowUp, ArrowDown, Copy } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

export const PLACEMENTS = { customer_portal: "Customer Portal", landing_page: "Landing Page", both: "Both" };
const EMPTY = { title: "", description: "", source: "Zola", expiry_date: "", display_on: "customer_portal", image_url: "",
                price: "", price_currency: "AED", category: "", partner_name: "", discount: "", sections: ["deals"],
                featured_badge: "", featured_subtitle: "", featured_seal: "", featured_cta: "", featured_start: "", featured_end: "" };
export const SECTIONS = { featured: "Featured Partner", deals: "Exclusive Member Deals" };
const FEATURED_KEYS = ["featured_badge", "featured_subtitle", "featured_seal", "featured_cta", "featured_start", "featured_end"];
const secs = (o) => o.sections?.length ? o.sections : ["deals"];

function SectionPicker({ form, setForm }) {
  const toggle = (k, on) => setForm({ ...form, sections: on ? [...new Set([...form.sections, k])] : form.sections.filter((x) => x !== k) });
  return (
    <div className="space-y-1.5">
      <Label>Display section *</Label>
      <div className="flex flex-col gap-2 rounded-xl p-3" style={{ border: "1px solid var(--border)" }}>
        {Object.entries(SECTIONS).map(([k, v]) => (
          <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={form.sections.includes(k)} onCheckedChange={(on) => toggle(k, !!on)} data-testid={`new-offer-section-${k}`} />{v}
          </label>
        ))}
      </div>
      {!form.sections.length && <p className="text-xs text-red-600" data-testid="new-offer-section-error">Choose at least one section.</p>}
    </div>
  );
}

function FeaturedFields({ form, setForm }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const field = (k, label, max, ph) => (
    <div className="space-y-1.5"><Label>{label}</Label>
      <Input value={form[k] || ""} onChange={set(k)} maxLength={max} placeholder={ph} data-testid={`new-offer-${k.replace("_", "-")}`} /></div>
  );
  return (
    <div className="space-y-3 rounded-xl p-3" style={{ background: "var(--bg-soft)", border: "1px solid var(--border)" }} data-testid="featured-fields">
      <div className="label-caps">Featured Partner banner</div>
      {field("featured_badge", "Badge text", 60, "Featured Partner of the Month")}
      {field("featured_subtitle", "Subtitle", 200, "Leave blank to use the offer text")}
      <div className="grid grid-cols-2 gap-2">
        {field("featured_seal", "Seal text", 40, "Special Member Rates")}
        {field("featured_cta", "CTA label", 40, "Claim Offer")}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1.5"><Label>Start (optional)</Label>
          <Input type="date" value={form.featured_start || ""} onChange={set("featured_start")} data-testid="new-offer-featured-start" /></div>
        <div className="space-y-1.5"><Label>End (optional)</Label>
          <Input type="date" value={form.featured_end || ""} min={form.featured_start || undefined} onChange={set("featured_end")} data-testid="new-offer-featured-end" /></div>
      </div>
    </div>
  );
}

const statusCls = (s) => s === "Approved" ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
  : s === "Rejected" ? "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300"
  : "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300";

function OfferForm({ form, setForm, editing, onCancel, onSubmit, currencies }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <form onSubmit={onSubmit} className="w-card w-card-bordered p-6 space-y-4 self-start" data-testid="new-offer-form">
      <h3 className="text-lg font-semibold">{editing ? "Edit offer" : "Add offer"}</h3>
      <div className="space-y-1.5">
        <Label>Title</Label>
        <Input value={form.title} onChange={set("title")} required data-testid="new-offer-title" />
      </div>
      <div className="space-y-1.5">
        <Label>Description</Label>
        <Textarea rows={3} value={form.description} onChange={set("description")} required data-testid="new-offer-description" />
      </div>
      <div className="space-y-1.5">
        <Label>Price (optional)</Label>
        <div className="flex gap-2">
          <Select value={form.price_currency} onValueChange={(v) => setForm({ ...form, price_currency: v })}>
            <SelectTrigger className="w-24" data-testid="new-offer-price-currency"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[...new Set(["AED", ...currencies])].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="number" min="0" step="0.01" placeholder="e.g. 949" value={form.price}
                 onChange={set("price")} data-testid="new-offer-price" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Deal category (landing filter chips)</Label>
        <Input list="offer-categories" value={form.category || ""} onChange={set("category")} placeholder="e.g. Restaurants"
               data-testid="new-offer-category" />
        <datalist id="offer-categories">{["Restaurants", "Hotels", "Cars", "Yachts", "Attractions"].map((c) => <option key={c} value={c} />)}</datalist>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1.5">
          <Label>Partner name (optional)</Label>
          <Input value={form.partner_name || ""} onChange={set("partner_name")} maxLength={60} placeholder="e.g. Atlantis The Palm"
                 data-testid="new-offer-partner-name" />
        </div>
        <div className="space-y-1.5">
          <Label>Discount (optional)</Label>
          <Input value={form.discount || ""} onChange={set("discount")} maxLength={40} placeholder="e.g. 20% OFF"
                 data-testid="new-offer-discount" />
        </div>
      </div>
      <SectionPicker form={form} setForm={setForm} />
      {form.sections.includes("featured") && <FeaturedFields form={form} setForm={setForm} />}
      <OfferImageField value={form.image_url} onChange={(url) => setForm((f) => ({ ...f, image_url: url }))} />
      <div className="space-y-1.5">
        <Label>Display on *</Label>
        <Select value={form.display_on} onValueChange={(v) => v && setForm((f) => ({ ...f, display_on: v }))}>
          <SelectTrigger data-testid="new-offer-display-on"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(PLACEMENTS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Source</Label>
          <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
            <SelectTrigger data-testid="new-offer-source"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Zola">Zola</SelectItem>
              <SelectItem value="Partner">Partner</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Expiry (optional)</Label>
          <Input type="date" value={form.expiry_date || ""} onChange={set("expiry_date")} data-testid="new-offer-expiry" />
        </div>
      </div>
      <div className="flex gap-2">
        {editing && <Button type="button" variant="outline" className="rounded-xl h-11" onClick={onCancel}
                            data-testid="offer-edit-cancel">Cancel</Button>}
        <Button type="submit" disabled={!form.sections.length} className="flex-1 bg-navy text-white rounded-xl h-11" data-testid="offer-submit-btn">
          {editing ? "Save changes" : "Publish offer"}
        </Button>
      </div>
    </form>
  );
}

function RowActions({ o, onEdit, refresh, saveWithReplace }) {
  const act = (fn, msg) => async () => {
    try { await fn(); toast.success(msg); refresh(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
      <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => onEdit(o)}
              data-testid={`edit-offer-${o.id}`}>Edit</Button>
      <Button size="sm" variant="outline" className="rounded-full h-8" data-testid={`publish-offer-${o.id}`}
              onClick={() => saveWithReplace((r) => api.patch(`/offers/${o.id}`, { active: !o.active, ...r }), o.active ? "Offer unpublished" : "Offer published")}>
        {o.active ? "Unpublish" : "Publish"}</Button>
      <Button size="sm" variant="outline" className="rounded-full h-8" data-testid={`duplicate-offer-${o.id}`} aria-label="Duplicate"
              onClick={act(() => api.post(`/offers/${o.id}/duplicate`), "Offer duplicated as an unpublished draft")}><Copy className="h-3.5 w-3.5" /></Button>
      {o.approval_status !== "Approved" && (
        <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full h-8" data-testid={`approve-offer-${o.id}`}
                onClick={() => saveWithReplace((r) => api.patch(`/offers/${o.id}`, { approval_status: "Approved", ...r }), "Offer approved")}>Approve</Button>
      )}
      {o.approval_status !== "Rejected" && (
        <Button size="sm" variant="outline" className="rounded-full h-8" data-testid={`reject-offer-${o.id}`}
                onClick={act(() => api.patch(`/offers/${o.id}`, { approval_status: "Rejected" }), "Offer rejected")}>Reject</Button>
      )}
      <Button size="sm" variant="outline" data-testid={`delete-offer-${o.id}`}
              className="rounded-full h-8 border-red-300 text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
              onClick={() => window.confirm(`Delete offer "${o.title}"? This removes it everywhere it is displayed.`)
                && act(() => api.delete(`/offers/${o.id}`), "Offer deleted")()}>Delete</Button>
    </td>
  );
}

export default function OffersPanel({ offers, refresh, currencies = [], preset }) {
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [placement, setPlacement] = useState("all");
  const [section, setSection] = useState("all");
  const [sort, setSort] = useState(null);

  useEffect(() => {
    if (!preset) return;
    setEditingId(null);
    setForm({ ...EMPTY, sections: [preset.section], display_on: "both" });
    setSection(preset.section);
    setTimeout(() => document.querySelector("[data-testid=new-offer-form]")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  }, [preset]);

  const rows = useMemo(() => {
    const list = offers.filter((o) => (placement === "all" || (o.display_on || "customer_portal") === placement)
      && (section === "all" || secs(o).includes(section)));
    return sort ? [...list].sort((a, b) => sort.dir * ((b[sort.key] || 0) - (a[sort.key] || 0))) : list;
  }, [offers, placement, section, sort]);
  const canReorder = section !== "all" && !sort && placement === "all";

  // Retries once with replace_featured after the admin confirms replacing the current Featured Partner
  const saveWithReplace = async (call, msg) => {
    try {
      await call({});
    } catch (err) {
      const d = err?.response?.status === 409 && err.response.data?.detail;
      if (!d?.code) { toast.error(formatError(err)); return false; }
      if (!window.confirm(`${d.message}. Replace it with this offer? It will stay in its other sections.`)) return false;
      try { await call({ replace_featured: true }); } catch (e2) { toast.error(formatError(e2)); return false; }
    }
    toast.success(msg);
    refresh();
    return true;
  };

  const move = async (idx, dir) => {
    const ids = rows.map((o) => o.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    try { await api.put("/offers/reorder", { ids }); refresh(); } catch (e) { toast.error(formatError(e)); }
  };

  const toggleSort = (key) => setSort((s) => (s?.key === key ? (s.dir === 1 ? { key, dir: -1 } : null) : { key, dir: 1 }));

  const startEdit = (o) => {
    setEditingId(o.id);
    setForm({ ...EMPTY, ...o, expiry_date: o.expiry_date ? o.expiry_date.slice(0, 10) : "", image_url: o.image_url || "",
              display_on: o.display_on || "customer_portal", price: o.price ?? "", price_currency: o.price_currency || "AED", category: o.category || "",
              partner_name: o.partner_name || "", discount: o.discount || "", sections: secs(o),
              ...Object.fromEntries(FEATURED_KEYS.map((k) => [k, o[k] || ""])) });
  };
  const reset = () => { setEditingId(null); setForm(EMPTY); };

  const submit = async (e) => {
    e.preventDefault();
    const body = { title: form.title, description: form.description, source: form.source,
                   expiry_date: form.expiry_date || null, display_on: form.display_on,
                   price_currency: form.price_currency, category: (form.category || "").trim(),
                   partner_name: (form.partner_name || "").trim(), discount: (form.discount || "").trim(), sections: form.sections,
                   ...Object.fromEntries(FEATURED_KEYS.map((k) => [k, form.sections.includes("featured") ? (form[k] || "").trim() : ""])) };
    const price = form.price === "" ? null : Number(form.price);
    const ok = editingId
      ? await saveWithReplace((r) => api.patch(`/offers/${editingId}`, { ...body, image_url: form.image_url || "", price: price || 0, ...r }), "Offer updated")
      : await saveWithReplace((r) => api.post("/offers", { ...body, image_url: form.image_url || null, price: price || null, category: body.category || null,
                                                           partner_name: body.partner_name || null, discount: body.discount || null, ...r }),
                              "Offer submitted (auto-approved as admin)");
    if (ok) reset();
  };

  const SortTh = ({ k, children }) => (
    <button type="button" className="inline-flex items-center gap-1 label-caps" onClick={() => toggleSort(k)}
            data-testid={`offers-sort-${k}`}>
      {children} <ArrowUpDown className={`h-3 w-3 ${sort?.key === k ? "opacity-100" : "opacity-40"}`} />
    </button>
  );

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-6">
      <div>
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h2 className="text-xl font-bold">Offers</h2>
          <div className="flex gap-2 flex-wrap">
          <Select value={section} onValueChange={setSection}>
            <SelectTrigger className="w-56" data-testid="offers-section-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sections</SelectItem>
              {Object.entries(SECTIONS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={placement} onValueChange={setPlacement}>
            <SelectTrigger className="w-48" data-testid="offers-placement-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All placements</SelectItem>
              {Object.entries(PLACEMENTS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          </div>
        </div>
        <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }} data-testid="offers-reorder-hint">
          {canReorder ? "Use the arrows to set the order of offers in this section." : "Pick a single section (and no sorting or placement filter) to reorder."}
        </p>
        <div className="w-card-flat overflow-x-auto">
          <table className="w-full text-sm" data-testid="offers-table">
            <thead style={{ background: "var(--bg-soft)" }}>
              <tr>
                <th className="text-left px-4 py-3 label-caps">Offer</th>
                <th className="text-left px-4 py-3 label-caps">Section</th>
                <th className="text-left px-4 py-3 label-caps">Display on</th>
                <th className="text-left px-4 py-3 label-caps">Status</th>
                <th className="text-left px-4 py-3">
                  <div className="label-caps">WhatsApp enquiries</div>
                  <div className="flex gap-3 mt-1"><SortTh k="enquiries_total">Total</SortTh><SortTh k="enquiries_30d">30d</SortTh></div>
                </th>
                <th className="text-right px-4 py-3 label-caps">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-sm" style={{ color: "var(--text-muted)" }}>No offers yet.</td></tr>
              )}
              {rows.map((o, idx) => (
                <tr key={o.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`offer-row-${o.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-start gap-3">
                      {canReorder && (
                        <div className="flex flex-col -my-1">
                          <button type="button" aria-label="Move up" disabled={idx === 0} onClick={() => move(idx, -1)} data-testid={`offer-up-${o.id}`}
                                  className="h-6 w-6 grid place-items-center rounded disabled:opacity-30 hover:bg-[color:var(--bg-soft)]"><ArrowUp className="h-3.5 w-3.5" /></button>
                          <button type="button" aria-label="Move down" disabled={idx === rows.length - 1} onClick={() => move(idx, 1)} data-testid={`offer-down-${o.id}`}
                                  className="h-6 w-6 grid place-items-center rounded disabled:opacity-30 hover:bg-[color:var(--bg-soft)]"><ArrowDown className="h-3.5 w-3.5" /></button>
                        </div>
                      )}
                      <OfferThumb src={o.image_url} className="w-12 flex-shrink-0 rounded-lg" />
                      <div>
                        <div className="font-medium">{o.title}</div>
                        {formatPrice(o) && <div className="text-xs font-semibold" style={{ color: "var(--gold)" }}>{formatPrice(o)}</div>}
                        <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                          {o.source}{o.category ? ` · ${o.category}` : ""} · {o.submitted_by || o.created_by} · {new Date(o.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-testid={`offer-sections-${o.id}`}>
                    <div className="flex flex-col gap-1 items-start">
                      {secs(o).map((s) => (
                        <span key={s} className="text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap font-medium"
                              style={s === "featured" ? { background: "var(--gold)", color: "var(--navy)" } : { background: "var(--bg-soft)", border: "1px solid var(--border)" }}>
                          {SECTIONS[s]}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full whitespace-nowrap"
                          style={{ background: "var(--bg-soft)", border: "1px solid var(--border)" }}
                          data-testid={`offer-placement-${o.id}`}>
                      {PLACEMENTS[o.display_on || "customer_portal"]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${statusCls(o.approval_status)}`}>{o.approval_status}</span>
                    <div className="mt-1 text-[11px] font-medium" style={{ color: o.active ? "#047857" : "var(--text-muted)" }} data-testid={`offer-published-${o.id}`}>
                      {o.active ? "Published" : "Draft"}</div>
                  </td>
                  <td className="px-4 py-3" data-testid={`offer-enquiries-${o.id}`}>
                    <div className="font-semibold">{o.enquiries_total || 0}</div>
                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>{o.enquiries_30d || 0} in 30 days</div>
                  </td>
                  <RowActions o={o} onEdit={startEdit} refresh={refresh} saveWithReplace={saveWithReplace} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <OfferForm form={form} setForm={setForm} editing={!!editingId} onCancel={reset} onSubmit={submit}
                 currencies={currencies} />
    </div>
  );
}
